// ============================================================================
// FlagshipShowcase - our two PRIMARY business models displayed at the top of
// the homepage. No mock listings; feature-driven explanation of what each
// product is + how it works + clear CTA.
// ============================================================================
import React from 'react';
import { useNavigate } from 'react-router-dom';

const Card = ({ tone, badge, title, tagline, bullets, primaryCta, secondaryCta, accent, illustration }) => {
  const navigate = useNavigate();
  return (
    <div
      className={`relative overflow-hidden rounded-3xl border shadow-sm hover:shadow-xl transition-all duration-300`}
      style={{
        background: tone === 'dark'
          ? 'linear-gradient(135deg, #0f0e2a 0%, #1e1b4b 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
        borderColor: tone === 'dark' ? '#1e1b4b' : '#e2e8f0',
        color: tone === 'dark' ? '#fff' : '#1e293b',
      }}
    >
      {/* Decorative accent stripe */}
      <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-20 blur-3xl"
        style={{ background: accent, transform: 'translate(40%, -40%)' }} />

      <div className="relative p-8 lg:p-10">
        <div className="flex items-center gap-3 mb-4">
          <span style={{
            fontSize: 11, fontWeight: 700, letterSpacing: 2,
            padding: '4px 10px', borderRadius: 20,
            background: tone === 'dark' ? accent : '#eef2ff',
            color: tone === 'dark' ? '#0f0e2a' : accent,
          }}>{badge}</span>
          <span style={{ fontSize: 24 }}>{illustration}</span>
        </div>

        <h3 className="text-2xl lg:text-3xl font-extrabold leading-tight mb-2"
          style={{ color: tone === 'dark' ? '#fff' : '#0f172a' }}>
          {title}
        </h3>
        <p className="text-base mb-5 opacity-90"
          style={{ color: tone === 'dark' ? '#cbd5e1' : '#475569' }}>
          {tagline}
        </p>

        <ul className="space-y-2 mb-6">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <span style={{ color: accent, marginTop: 2 }}>✓</span>
              <span style={{ color: tone === 'dark' ? '#e2e8f0' : '#334155' }}>{b}</span>
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate(primaryCta.link)}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold shadow hover:shadow-lg transition-all"
            style={{ background: accent, color: tone === 'dark' ? '#0f0e2a' : '#fff' }}
          >
            {primaryCta.text} →
          </button>
          {secondaryCta && (
            <button
              onClick={() => navigate(secondaryCta.link)}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold transition-all"
              style={{
                background: 'transparent',
                color: tone === 'dark' ? '#fff' : accent,
                border: `2px solid ${tone === 'dark' ? 'rgba(255,255,255,0.3)' : accent}`,
              }}
            >
              {secondaryCta.text}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const FlagshipShowcase = () => {
  return (
    <section className="py-16 lg:py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <span style={{
            display: 'inline-block', fontSize: 12, fontWeight: 700, letterSpacing: 2,
            color: '#4f46e5', padding: '4px 12px', borderRadius: 20,
            background: '#eef2ff', marginBottom: 12,
          }}>WHAT WE DO</span>
          <h2 className="text-3xl lg:text-4xl font-extrabold mb-3 text-slate-900">
            Common Channels for Creators
          </h2>
          <p className="text-base lg:text-lg text-slate-600">
            We run multi-creator common channels across short films, music videos, stand-up comedy,
            web series, documentaries and more. Each creator keeps 70-80% of their per-video revenue,
            paid monthly with transparent reporting.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          {/* Common Channel - the single soft-launch product */}
          <Card
            tone="light"
            badge="FOR CREATORS"
            illustration="📺"
            accent="#4f46e5"
            title="Common Channels - Multi-Category"
            tagline="One channel, many creators. We run common channels across short films, music videos, stand-up comedy, web series, documentaries and more. Each creator keeps 70-80% of their per-video revenue - monthly, transparently."
            bullets={[
              "Channels across many categories: short films, music videos, stand-up comedy, web series, documentaries, vlogs and more",
              "Keep 70-80% of YOUR video's ad revenue (per-creator share)",
              "Monthly payouts with PDF invoices",
              "Automatic TDS withholding where applicable (India 10% with PAN); clean cross-border payouts worldwide",
              "Channel owner handles thumbnails, SEO, audience growth",
              "Quota and earnings visible in your dashboard 24/7"
            ]}
            primaryCta={{ text: "Apply as Creator", link: "/apply" }}
            secondaryCta={{ text: "Channels I'm on", link: "/my-channels" }}
          />
        </div>
      </div>
    </section>
  );
};

export default FlagshipShowcase;
